import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TwitterShareButton, EmailShareButton } from 'react-share';
import { XIcon, EmailIcon } from 'react-share';
import axiosInstance from '../axiosConfig';
import './ReferralDashboard.css';

const ReferralDashboard = () => {
  const [referralCode, setReferralCode] = useState('');
  const [referralUrl, setReferralUrl] = useState('');
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total: 0, pending: 0, completed: 0, rewarded: 0 });
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rewardEligibleReferrals, setRewardEligibleReferrals] = useState([]);
  const [addresses, setAddresses] = useState([]);
  const [selectedAddress, setSelectedAddress] = useState('');
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [newAddress, setNewAddress] = useState({
    name: '',
    street_address: '',
    apartment: '',
    city: '',
    state: '',
    zip_code: '',
    is_default: false
  });
  const navigate = useNavigate();
  const hiddenInputRef = useRef(null);

  useEffect(() => {
    const fetchReferralData = async () => {
      try {
        // Check if user is authenticated
        const token = localStorage.getItem('access_token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Get user's referral code
        const codeResponse = await axiosInstance.get('/referral-code/');
        setReferralCode(codeResponse.data.code);
        setReferralUrl(codeResponse.data.referral_url);

        // Get user's referrals
        const referralsResponse = await axiosInstance.get('/referrals/');
        setReferrals(referralsResponse.data.referrals);
        setStats(referralsResponse.data.stats);

        // Get eligible referrals (completed but no address)
        const eligibleResponse = await axiosInstance.get('/reward-eligible-referrals/');
        setRewardEligibleReferrals(eligibleResponse.data.eligible_referrals);

        // If the user has no addresses but has eligible referrals, show the address form
        if (eligibleResponse.data.eligible_referrals.length > 0 && !eligibleResponse.data.has_addresses) {
          setShowAddressForm(true);
        }

        // Get user's addresses
        const addressesResponse = await axiosInstance.get('/shipping-addresses/');
        setAddresses(addressesResponse.data);

        // Set default address as selected if available
        const defaultAddress = addressesResponse.data.find(addr => addr.is_default);
        if (defaultAddress) {
          setSelectedAddress(defaultAddress.id);
        } else if (addressesResponse.data.length > 0) {
          setSelectedAddress(addressesResponse.data[0].id);
        }
      } catch (error) {
        console.error('Error fetching referral data:', error);
        setError('Unable to load referral data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReferralData();
  }, [navigate]);

  const copyToClipboard = () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Modern clipboard API
      navigator.clipboard.writeText(referralUrl).then(
        () => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        },
        (err) => {
          console.error('Failed to copy link: ', err);
          setError('Failed to copy to clipboard');
        }
      );
    } else {
      // Fallback for unsupported environments
      if (hiddenInputRef.current) {
        hiddenInputRef.current.value = referralUrl;
        hiddenInputRef.current.select();
        try {
          document.execCommand('copy');
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        } catch (err) {
          console.error('Fallback copy failed: ', err);
          setError('Failed to copy to clipboard');
        }
      }
    }
  };

  const shareViaEmail = () => {
    const subject = "Check out privateIQ!";
    const body = `I think you'll like privateIQ. Use my referral link to sign up: ${referralUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setNewAddress({
      ...newAddress,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post('/shipping-addresses/', newAddress);
      setAddresses([...addresses, response.data]);
      setSelectedAddress(response.data.id);
      setNewAddress({
        name: '',
        street_address: '',
        apartment: '',
        city: '',
        state: '',
        zip_code: '',
        is_default: false
      });
      setShowAddressForm(false);

      // If we were in the process of setting an address for a referral
      if (selectedReferral) {
        await handleSetReferralAddress(selectedReferral, response.data.id);
      }
    } catch (error) {
      console.error('Error saving address:', error);
      setError('Failed to save address. Please try again.');
    }
  };

  const handleSetReferralAddress = async (referralId, addressId = null) => {
    const addrId = addressId || selectedAddress;
    if (!addrId) {
      setError('Please select or add a shipping address');
      return;
    }

    try {
      await axiosInstance.post(`/referrals/${referralId}/set-address/`, {
        address_id: addrId
      });

      // Update the UI to remove this referral from eligible list
      setRewardEligibleReferrals(
        rewardEligibleReferrals.filter(ref => ref.id !== referralId)
      );

      // Close the modal
      setShowAddressModal(false);
      setSelectedReferral(null);

      // Show success message
      alert('Shipping address successfully saved. Your golf ball reward will be shipped soon!');

      // Refresh referrals data
      const referralsResponse = await axiosInstance.get('/referrals/');
      setReferrals(referralsResponse.data.referrals);
      setStats(referralsResponse.data.stats);
    } catch (error) {
      console.error('Error setting referral address:', error);
      setError('Failed to set shipping address for this referral');
    }
  };

  const openAddressModal = (referralId) => {
    setSelectedReferral(referralId);

    // If user has no addresses, show the address form
    if (addresses.length === 0) {
      setShowAddressForm(true);
    }

    setShowAddressModal(true);
  };

  if (loading) {
    return <div className="loading">Loading referral data...</div>;
  }

  return (
    <div className="referral-dashboard">
      <h2>Refer Friends, Get Golf Balls</h2>
      <p className="referral-intro">
        Share your unique link with friends. When they sign up and complete payment, we'll ship you a sleeve of ProV1s and maybe some other stuff.
      </p>

      {error && <p className="error-message">{error}</p>}

      {/* Address Modal */}
      {showAddressModal && (
        <div className="address-modal-overlay">
          <div className="address-modal">
            <h3>Provide Shipping Address</h3>
            <p>Please provide your shipping address to receive your golf ball reward.</p>

            {addresses.length > 0 && !showAddressForm && (
              <>
                <div className="address-selection">
                  <label htmlFor="address-select">Select Address:</label>
                  <select
                    id="address-select"
                    value={selectedAddress}
                    onChange={(e) => setSelectedAddress(e.target.value)}
                  >
                    {addresses.map(address => (
                      <option key={address.id} value={address.id}>
                        {address.name} - {address.street_address}, {address.city}, {address.state} {address.zip_code}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="address-form-buttons">
                  <button onClick={() => setShowAddressForm(true)} className="secondary-button">
                    Add New Address
                  </button>
                  <button
                    onClick={() => handleSetReferralAddress(selectedReferral)}
                    className="primary-button"
                  >
                    Use This Address
                  </button>
                </div>
              </>
            )}

            {showAddressForm && (
              <form onSubmit={handleAddressSubmit} className="address-form">
                <div className="form-group">
                  <label htmlFor="name">Full Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={newAddress.name}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="street_address">Street Address</label>
                  <input
                    type="text"
                    id="street_address"
                    name="street_address"
                    value={newAddress.street_address}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="apartment">Apartment/Suite (Optional)</label>
                  <input
                    type="text"
                    id="apartment"
                    name="apartment"
                    value={newAddress.apartment}
                    onChange={handleInputChange}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={newAddress.city}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="state">State</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={newAddress.state}
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="zip_code">ZIP Code</label>
                  <input
                    type="text"
                    id="zip_code"
                    name="zip_code"
                    value={newAddress.zip_code}
                    onChange={handleInputChange}
                    required
                  />
                </div>

                <div className="form-checkbox">
                  <input
                    type="checkbox"
                    id="is_default"
                    name="is_default"
                    checked={newAddress.is_default}
                    onChange={handleInputChange}
                  />
                  <label htmlFor="is_default">Set as default address</label>
                </div>

                <div className="form-buttons">
                  {addresses.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setShowAddressForm(false)}
                      className="secondary-button"
                    >
                      Cancel
                    </button>
                  )}
                  <button type="submit" className="primary-button">Save Address</button>
                </div>
              </form>
            )}

            <button
              className="close-modal-button"
              onClick={() => {
                setShowAddressModal(false);
                setSelectedReferral(null);
                setShowAddressForm(addresses.length === 0);
              }}
            >
              &times;
            </button>
          </div>
        </div>
      )}

      {/* Reward Alert - Show when there are eligible referrals */}
      {rewardEligibleReferrals.length > 0 && (
        <div className="reward-alert">
          <div className="reward-alert-content">
            <div className="reward-alert-icon">🎁</div>
            <div className="reward-alert-text">
              <h3>You've earned {rewardEligibleReferrals.length} golf ball reward{rewardEligibleReferrals.length > 1 ? 's' : ''}!</h3>
              <p>Provide your shipping address to claim your reward.</p>
            </div>
            <button onClick={() => openAddressModal(rewardEligibleReferrals[0].id)} className="claim-reward-button">
              Claim Now
            </button>
          </div>
        </div>
      )}

      <div className="referral-link-container">
        <h3>Your Referral Link</h3>
        <div className="referral-link-box">
          <input
            type="text"
            value={referralUrl}
            readOnly
            className="referral-link-input"
          />
          <button
            onClick={copyToClipboard}
            className="copy-button"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        {/* Hidden Input for Fallback */}
        <input
          type="text"
          ref={hiddenInputRef}
          style={{ position: 'absolute', left: '-9999px', opacity: 0 }}
          readOnly
        />

        <div className="share-options">
          {/* Twitter */}
          <TwitterShareButton
            url={referralUrl}
            title="Join me on privateIQ! Use my referral link:"
            className="share-button twitter-button"
          >
            <XIcon size={32} round />
          </TwitterShareButton>

          {/* Email using react-share */}
          <EmailShareButton
            url={referralUrl}
            subject="Join privateIQ with my referral"
            body="Check out privateIQ for golf club information. Use my referral link to sign up:"
            className="share-button email-share-button"
          >
            <EmailIcon size={32} round />
          </EmailShareButton>

          {/* Custom Email Option */}
          <button onClick={shareViaEmail} className="share-button custom-email-button">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor" width="24" height="24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
            <span>Email</span>
          </button>
        </div>
      </div>

      <div className="referral-stats">
        <h3>Your Referral Stats</h3>
        <div className="stats-container">
          <div className="stat-box">
            <span className="stat-number">{stats.total}</span>
            <span className="stat-label">Total Referrals</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">{stats.completed + stats.rewarded}</span>
            <span className="stat-label">Successful</span>
          </div>
          <div className="stat-box">
            <span className="stat-number">{stats.rewarded}</span>
            <span className="stat-label">Rewarded</span>
          </div>
        </div>
      </div>

      {rewardEligibleReferrals.length > 0 && (
        <div className="pending-rewards">
          <h3>Pending Rewards</h3>
          <div className="pending-rewards-list">
            {rewardEligibleReferrals.map(referral => (
              <div key={referral.id} className="pending-reward-item">
                <div className="reward-info">
                  <div className="reward-icon">🏌️‍♂️</div>
                  <div className="reward-details">
                    <span className="reward-user">{referral.referee_username} completed signup</span>
                    <span className="reward-date">{referral.completed_at}</span>
                  </div>
                </div>
                <button
                  onClick={() => openAddressModal(referral.id)}
                  className="ship-to-me-button"
                >
                  Ship to Me
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {referrals.length > 0 ? (
        <div className="referrals-list">
          <h3>Your Referrals</h3>
          <table className="referrals-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Date</th>
                <th>Status</th>
                <th>Reward</th>
              </tr>
            </thead>
            <tbody>
              {referrals.map((referral, index) => (
                <tr key={index}>
                  <td>{referral.referee_username}</td>
                  <td>{referral.created_at}</td>
                  <td>
                    <span className={`status-badge ${referral.status}`}>
                      {referral.status.charAt(0).toUpperCase() + referral.status.slice(1)}
                    </span>
                  </td>
                  <td>{referral.reward_sent ? 'Sent' : 'Pending'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-referrals">
          <p>You haven't referred anyone yet. Share your link to get started!</p>
        </div>
      )}
    </div>
  );
};

export default ReferralDashboard;